import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Plus, CheckCircle, XCircle, Search, Pencil, Trash2, FolderOpen, RefreshCw, Loader2, Download, Calendar, X, Upload, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/DashLayout";
import { api } from "@/lib/api";
import Pagination, { usePagination } from "@/components/Pagination";

import SearchableSelect from "@/components/ui/SearchableSelect";

const STATUS_FILTERS = ["all", "pending", "approved", "rejected"];

export default function Customers() {
  const [activeTab, setActiveTab] = useState("requests");
  const [customers, setCustomers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [agents, setAgents] = useState([]);

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", mobile: "", dth_id: "",
    operator: "Dish TV", dealer_id: "", agent_id: "", plan_id: "", pincode: "", area: ""
  });

  const [approveModal, setApproveModal] = useState(null);
  const [approveForm, setApproveForm] = useState({ customer_id: "", box_number: "", group_id: "", plan_id: "", assigned_dealer_id: "" });

  // Filters — requests
  const [reqStatus, setReqStatus] = useState("all");
  const [reqSearch, setReqSearch] = useState("");
  const [reqDealer, setReqDealer] = useState("all");

  // Edit modal
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "", email: "", mobile: "", status: "active",
    dth_id: "", box_number: "", operator: "Dish TV",
    dealer_id: "", group_id: "",
  });

  // Customers server-side pagination
  const [custTotal, setCustTotal] = useState(0);
  const [custPage, setCustPage] = useState(1);
  const [custSearchInput, setCustSearchInput] = useState("");
  const [custSearchApi, setCustSearchApi] = useState("");
  const [custDealer, setCustDealer] = useState("all");
  const [custStatus, setCustStatus] = useState("all");
  const [custDateFrom, setCustDateFrom] = useState("");
  const [custDateTo, setCustDateTo] = useState("");

  // Debounce customer search
  useEffect(() => {
    const t = setTimeout(() => { setCustSearchApi(custSearchInput); setCustPage(1); }, 400);
    return () => clearTimeout(t);
  }, [custSearchInput]);

  const [checkingApi, setCheckingApi] = useState(null);
  const [checkSubModal, setCheckSubModal] = useState(null);

  // Bulk upload state
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const handleCheckSub = async (connId) => {
    setCheckingApi(connId);
    try {
      const res = await api.post(`/admin/customers/${connId}/check-subscription`);
      setCheckSubModal(res.data.data);
      loadCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to check subscription");
    } finally {
      setCheckingApi(null);
    }
  };

  const loadCustomers = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', custPage);
    params.set('limit', 25);
    if (custSearchApi) params.set('search', custSearchApi);
    if (custDealer !== "all") params.set('dealer_id', custDealer);
    if (custStatus !== "all") params.set('status', custStatus);
    if (custDateFrom) params.set('date_from', custDateFrom);
    if (custDateTo) params.set('date_to', custDateTo);
    api.get(`/admin/customers?${params.toString()}`).then(r => {
      setCustomers(r.data.items || []);
      setCustTotal(r.data.total || 0);
    }).catch(() => { });
  }, [custPage, custSearchApi, custDealer, custStatus, custDateFrom, custDateTo]);

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      params.set('page', 1);
      params.set('limit', 10000);
      if (custSearchApi) params.set('search', custSearchApi);
      if (custDealer !== "all") params.set('dealer_id', custDealer);
      if (custStatus !== "all") params.set('status', custStatus);
      if (custDateFrom) params.set('date_from', custDateFrom);
      if (custDateTo) params.set('date_to', custDateTo);

      const r = await api.get(`/admin/customers?${params.toString()}`);
      const data = r.data.items || [];
      if (data.length === 0) {
        toast.info("No customers to export");
        return;
      }

      const headers = ["Name", "Mobile", "Email", "Status", "DTH ID", "Box Number", "Operator", "Plan", "Subscription Status", "Request Date", "Approve Date", "Reference"];
      const csvRows = [headers.join(",")];

      data.forEach(c => {
        const id = c.ids?.[0] || {};
        const sub = c.subs?.[0] || {};
        const plan = plans.find(p => p.id === sub.product_id);
        const planName = plan ? plan.name : "No Plan";
        const dthId = id.dth_id || "";
        const box = id.box_number || "";
        let ref = id.ref_type === "admin" ? "Admin" : `${id.ref_type || "Dealer"} - ${id.ref_name || "Unknown"}`;
        if (id.lead_agent_name) ref += ` (Agent: ${id.lead_agent_name})`;
        const reqDate = c.request_date ? new Date(c.request_date).toLocaleString('en-IN') : "-";
        const appDate = c.approve_date ? new Date(c.approve_date).toLocaleString('en-IN') : "-";

        csvRows.push([
          `"${c.user?.name || ""}"`,
          `"${c.user?.mobile || ""}"`,
          `"${c.user?.email || ""}"`,
          `"${c.status || ""}"`,
          `"${dthId}"`,
          `"${box}"`,
          `"${id.operator || ""}"`,
          `"${planName}"`,
          `"${sub.status || ""}"`,
          `"${reqDate}"`,
          `"${appDate}"`,
          `"${ref}"`
        ].join(","));
      });

      const blob = new Blob([csvRows.join("\n")], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `customers_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
    } catch (err) {
      toast.error("Failed to export data");
    }
  };

  const [reqTotal, setReqTotal] = useState(0);
  const [reqPage, setReqPage] = useState(1);
  const [reqSearchInput, setReqSearchInput] = useState("");
  const [reqSearchApi, setReqSearchApi] = useState("");

  // Debounce request search
  useEffect(() => {
    const t = setTimeout(() => { setReqSearchApi(reqSearchInput); setReqPage(1); }, 400);
    return () => clearTimeout(t);
  }, [reqSearchInput]);

  const loadRequests = useCallback(() => {
    const params = new URLSearchParams({ page: reqPage, limit: 25 });
    if (reqStatus !== "all") params.set('status', reqStatus);
    if (reqSearchApi) params.set('search', reqSearchApi);
    if (reqDealer !== "all") params.set('dealer_id', reqDealer); // Assuming reqDealer stores dealer_id if it's not "all"

    api.get(`/admin/registration-requests?${params.toString()}`).then(r => {
      setRequests(r.data.items || []);
      setReqTotal(r.data.total || 0);
    }).catch(() => { });
  }, [reqPage, reqStatus, reqSearchApi, reqDealer]);
  const loadDealers = () => api.get("/admin/dealers?limit=100").then(r => setDealers(r.data.items || r.data)).catch(() => { });
  const loadAgents = () => api.get("/admin/agents?limit=100").then(r => setAgents(r.data.items || r.data)).catch(() => { });
  const loadPlans = () => api.get("/admin/products").then(r => {
    const list = Array.isArray(r.data) ? r.data : (r.data.items || []);
    setPlans(list);
    if (list.length > 0) setForm(f => ({ ...f, plan_id: f.plan_id || list[0].id }));
  }).catch(() => { });
  const loadGroups = () => api.get("/admin/product-groups").then(r => setProductGroups(r.data)).catch(() => { });

  // ── Bulk Upload Handlers ──
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const res = await api.get("/admin/registration-requests/excel-template", { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customer_requests_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded!");
    } catch (err) {
      toast.error("Failed to download template");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      toast.error("Please select an Excel file");
      return;
    }
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      const res = await api.post("/admin/registration-requests/bulk-upload", formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBulkResult(res.data);
      if (res.data.success > 0) {
        toast.success(`${res.data.success} request(s) created and auto-approved successfully!`);
        loadRequests();
        loadCustomers();
      }
      if (res.data.failed > 0) {
        toast.error(`${res.data.failed} row(s) failed. See details below.`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to upload file";
      toast.error(msg);
      setBulkResult({ success: 0, failed: 0, errors: [{ row: 0, name: "Upload Error", errors: [msg] }] });
    } finally {
      setBulkUploading(false);
    }
  };

  useEffect(() => {
    loadCustomers(); loadRequests(); loadDealers(); loadAgents(); loadPlans(); loadGroups();
  }, [loadCustomers, loadRequests, custDealer, custStatus, custDateFrom, custDateTo]);

  const isValidMobile = (num) => /^[6-9]\d{9}$/.test(num);

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!isValidMobile(form.mobile)) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    try {
      // Strip empty email so backend treats it as null
      const payload = { ...form, dealer_id: form.dealer_id || null, agent_id: form.agent_id || null };
      if (!payload.email || !payload.email.trim()) delete payload.email;
      await api.post("/admin/registration-requests", payload);
      toast.success("Registration request submitted");
      setShowRequestForm(false);
      setForm({ name: "", email: "", mobile: "", dth_id: "", operator: "Dish TV", dealer_id: "", agent_id: "", plan_id: plans[0]?.id || "", pincode: "", area: "" });
      loadRequests();
      setActiveTab("requests");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const handleApprove = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...approveForm, group_id: approveForm.group_id || null, plan_id: approveForm.plan_id || null };
      if (!payload.assigned_dealer_id) delete payload.assigned_dealer_id;
      await api.post(`/admin/registration-requests/${approveModal.id}/approve`, payload);
      toast.success("Request approved and customer created!");
      setApproveModal(null);
      setApproveForm({ customer_id: "", box_number: "", group_id: "", plan_id: "", assigned_dealer_id: "" });
      loadRequests();
      loadCustomers();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to approve"); }
  };

  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const openRejectModal = (req) => {
    setRejectModal(req);
    setRejectReason("");
  };

  const submitReject = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/registration-requests/${rejectModal.id}/reject`, { reason: rejectReason });
      toast.success("Request rejected");
      setRejectModal(null);
      loadRequests();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to reject"); }
  };

  const openApproveModal = (req) => {
    setApproveModal(req);
    const planId = req.plan_id || req.plan?.id || "";
    const plan = plans.find(p => p.id === planId);
    const groupId = plan?.group_id || "";
    setApproveForm({ customer_id: req.dth_id || "", box_number: req.box_number || "", group_id: groupId, plan_id: planId, assigned_dealer_id: "" });
  };

  const openEditModal = (c) => {
    const conn = (c.ids || [])[0] || {};
    const sub = (c.subs || [])[0] || {};
    setEditModal(c);
    setEditForm({
      name: c.user?.name || "", email: c.user?.email || "", mobile: c.user?.mobile || "",
      status: c.status || "active",
      dth_id: conn.dth_id || "", box_number: conn.box_number || "",
      operator: conn.operator || "Dish TV",
      dealer_id: conn.ref_type === 'admin' ? '' : (conn.ref_id || ''),
      group_id: conn.group_id || '',
      plan_id: sub.product_id || '',
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/customers/${editModal.id}`, editForm);
      toast.success("Customer updated");
      setEditModal(null);
      loadCustomers();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to update"); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete customer "${c.user?.name}"? This will deactivate their account and all DTH connections.`)) return;
    try {
      await api.delete(`/admin/customers/${c.id}`);
      toast.success("Customer deleted");
      loadCustomers();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to delete"); }
  };

  // ── Filtered active customers ──
  const custPageCount = Math.max(1, Math.ceil(custTotal / 25));
  const reqPageCount = Math.max(1, Math.ceil(reqTotal / 25));

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav">Customers & Requests</h3>
          <p className="text-slate-500 text-sm mt-1">Manage registration requests and active customers.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={handleDownloadTemplate} disabled={downloadingTemplate}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 rounded-md shadow-sm transition-colors disabled:opacity-50">
            {downloadingTemplate ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Download Excel Format
          </button>
          <button onClick={() => { setShowBulkUpload(true); setBulkFile(null); setBulkResult(null); }}
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-4 py-2 rounded-md shadow-sm transition-colors">
            <Upload className="w-4 h-4" /> Bulk Upload
          </button>
          <button onClick={() => setShowRequestForm(!showRequestForm)}
            className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold px-4 py-2 rounded-md shadow-sm">
            <Plus className="w-4 h-4" /> Add Request
          </button>
        </div>
      </div>

      {/* ── ADD REQUEST FORM ── */}
      {showRequestForm && (
        <form onSubmit={submitRequest} className="bg-white border border-slate-200 rounded-xl p-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Name *</label><input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2" /></div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Mobile *</label>
            <input
              required type="tel" placeholder="10-digit mobile" maxLength={10}
              value={form.mobile}
              onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              className={`w-full border rounded-md px-3 py-2 ${form.mobile && !isValidMobile(form.mobile) ? 'border-red-400' : 'border-slate-200'
                }`}
            />
            {form.mobile && !isValidMobile(form.mobile) && (
              <p className="text-xs text-red-500 mt-0.5">Enter valid 10-digit mobile (starts with 6–9)</p>
            )}
          </div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Email</label><input type="email" placeholder="Email (Optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2" /></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">DTH ID (Optional)</label><input placeholder="DTH ID" value={form.dth_id} onChange={(e) => setForm({ ...form, dth_id: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2 font-mono" /></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Operator *</label><select value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2">
            <option>Dish TV</option>
          </select></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Select DTH Plan *</label><select required value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2">
            <option value="" disabled>Select Plan</option>
            {plans.filter(p => p.plan_type !== 'recharge').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Pincode *</label><input required placeholder="Pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2" /></div>
          <div><label className="block text-xs font-medium text-slate-600 mb-1">Area / Locality *</label><input required placeholder="Area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="w-full border border-slate-200 rounded-md px-3 py-2" /></div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Assign to Dealer</label>
            <SearchableSelect
              value={form.dealer_id}
              onChange={val => setForm({ ...form, dealer_id: val })}
              options={[{ label: "Direct to admin", value: "" }, ...dealers.map(d => ({ label: `${d.user?.name} (${d.type})`, value: d.id }))]}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Lead from Agent</label>
            <SearchableSelect
              value={form.agent_id}
              onChange={val => setForm({ ...form, agent_id: val })}
              options={[{ label: "None", value: "" }, ...agents.map(a => ({ label: `${a.user?.name}`, value: a.id }))]}
            />
          </div>
          <div className="md:col-span-3 flex justify-end gap-3">
            <button type="button" onClick={() => setShowRequestForm(false)} className="border border-slate-200 text-slate-600 rounded-md px-5 py-2 hover:bg-slate-50 text-sm font-semibold">Cancel</button>
            <button className="bg-brand-accent hover:bg-brand-accentHover text-white font-semibold rounded-md px-6 py-2 text-sm">Submit Request</button>
          </div>
        </form>
      )}

      {/* ── TABS ── */}
      <div className="flex gap-4 border-b border-slate-200 mb-4">
        <button onClick={() => setActiveTab("requests")} className={`pb-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'requests' ? 'border-brand-primary text-brand-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
          Registration Requests
          <span className="ml-1.5 text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{requests.length}</span>
        </button>
        <button onClick={() => setActiveTab("active")} className={`pb-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'active' ? 'border-brand-primary text-brand-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
          Active Customers
          <span className="ml-1.5 text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{custTotal}</span>
        </button>
      </div>

      {/* ── REQUESTS TAB ── */}
      {activeTab === "requests" && (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            {/* Status pills */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {STATUS_FILTERS.map(s => (
                <button key={s} onClick={() => setReqStatus(s)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${reqStatus === s
                      ? s === "pending" ? "bg-amber-500 text-white"
                        : s === "approved" ? "bg-green-600 text-white"
                          : s === "rejected" ? "bg-red-500 text-white"
                            : "bg-brand-primary text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}>
                  {s === "all" ? `All (${reqTotal})` : `${s}`}
                </button>
              ))}
            </div>
            {/* Search + dealer filter */}
            <div className="flex gap-2 sm:ml-auto">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input placeholder="Search name, mobile, area…" value={reqSearchInput} onChange={e => setReqSearchInput(e.target.value)}
                  className="pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/30 w-48" />
              </div>
              {dealers.length > 0 && (
                <div className="w-48">
                  <SearchableSelect
                    value={reqDealer}
                    onChange={setReqDealer}
                    options={[{ label: "All Dealers", value: "all" }, ...dealers.map(d => ({ label: d.user?.name || "Unknown", value: d.id }))]}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col">
            <div className="overflow-auto max-h-[65vh]">
              <table className="w-full text-sm min-w-max">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th className="text-left px-6 py-3">Customer</th>
                    <th className="text-left px-6 py-3">Service</th>
                    <th className="text-left px-6 py-3">Source</th>
                    <th className="text-left px-6 py-3">Agent</th>
                    <th className="text-left px-6 py-3">Status</th>
                    <th className="text-left px-6 py-3">Request Date</th>
                    <th className="text-left px-6 py-3">Approve Date</th>
                    <th className="text-right px-6 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-brand-nav">{r.name}</div>
                        <div className="text-xs text-slate-500">{r.mobile} • {r.email || 'No email'}</div>
                        <div className="text-xs text-slate-400">{r.area} ({r.pincode})</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">{r.operator}</div>
                        <div className="text-xs text-slate-500">Plan: {r.plan?.name}</div>
                        {r.dth_id && <div className="text-xs font-mono text-slate-400">ID: {r.dth_id}</div>}
                      </td>
                      <td className="px-6 py-4">
                        {r.dealer ? (
                          <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-full border border-blue-100">Dealer: {r.dealer.name}</span>
                        ) : (
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full border border-slate-200">Admin</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {r.agent ? (
                          <span className="text-xs text-slate-600 font-medium">{r.agent.name}</span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {r.status === 'pending' && <Badge tone="warn">Pending</Badge>}
                        {r.status === 'approved' && <Badge tone="success">Approved</Badge>}
                        {r.status === 'rejected' && (
                          <div>
                            <Badge tone="danger">Rejected</Badge>
                            {r.rejection_reason && <div className="text-xs text-slate-400 mt-1">{r.rejection_reason}</div>}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs text-slate-500">
                          {new Date(r.created_at).toLocaleString('en-IN', {
                            year: 'numeric', month: 'short', day: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {r.approved_at ? (
                          <div className="text-xs text-slate-500">
                            {new Date(r.approved_at).toLocaleString('en-IN', {
                              year: 'numeric', month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {r.status === 'pending' && (
                          <div className="flex justify-end gap-2">
                            <button onClick={() => openApproveModal(r)} className="text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 transition-colors">
                              <CheckCircle className="w-3 h-3" /> Approve
                            </button>
                            <button onClick={() => openRejectModal(r)} className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 transition-colors">
                              <XCircle className="w-3 h-3" /> Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {requests.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-10 text-slate-400 text-sm">
                      {requests.length === 0 ? "No requests yet" : "No requests match your filters"}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="border-t border-slate-200 bg-slate-50">
              <Pagination page={reqPage} pageCount={reqPageCount} onChange={setReqPage} total={reqTotal} />
            </div>
          </div>
        </>
      )}

      {/* ── ACTIVE CUSTOMERS TAB ── */}
      {activeTab === "active" && (
        <>
          {/* Filters */}
          <div className="flex flex-col xl:flex-row gap-3 mb-4 xl:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input placeholder="Search by name, mobile, email, DTH ID…" value={custSearchInput} onChange={e => setCustSearchInput(e.target.value)}
                className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30" />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400 shrink-0 hidden sm:block" />
                <input type="date" value={custDateFrom} onChange={e => { setCustDateFrom(e.target.value); setCustPage(1); }}
                  className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                  title="From Date" />
                <span className="text-xs text-slate-400">to</span>
                <input type="date" value={custDateTo} onChange={e => { setCustDateTo(e.target.value); setCustPage(1); }}
                  className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                  title="To Date" />
                {(custDateFrom || custDateTo) && (
                  <button onClick={() => { setCustDateFrom(""); setCustDateTo(""); setCustPage(1); }}
                    className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors" title="Clear dates">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <select value={custStatus} onChange={e => { setCustStatus(e.target.value); setCustPage(1); }}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30">
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <div className="w-48">
                <SearchableSelect
                  value={custDealer}
                  onChange={val => { setCustDealer(val); setCustPage(1); }}
                  options={[
                    { label: "All Sources", value: "all" },
                    { label: "Direct to Admin", value: "admin" },
                    ...dealers.map(d => ({ label: `${d.user?.name} (${d.type})`, value: d.id }))
                  ]}
                />
              </div>

              <button onClick={handleExportCSV} disabled={custTotal === 0}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 border border-slate-200">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col">
            <div className="overflow-auto max-h-[65vh]">
              <table className="w-full text-sm min-w-max">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th className="text-left px-6 py-3">Customer</th>
                    <th className="text-left px-6 py-3">DTH</th>
                    <th className="text-left px-6 py-3">Plan Details</th>
                    <th className="text-left px-6 py-3">Subscription</th>
                    <th className="text-left px-6 py-3">Reference</th>
                    <th className="text-left px-6 py-3">Request Date</th>
                    <th className="text-left px-6 py-3">Approve Date</th>
                    <th className="text-right px-6 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-brand-nav">{c.user?.name}</div>
                        <div className="text-xs text-slate-500">{c.user?.email}</div>
                        <div className="text-xs text-slate-400">{c.user?.mobile}</div>
                      </td>
                      <td className="px-6 py-4">
                        {(c.ids || []).map((id) => (
                          <div key={id.id} className="text-sm">
                            <span className="font-mono">{id.dth_id}</span>
                            <span className="text-xs text-slate-400 ml-2">{id.operator}</span>
                            {id.group_id && (() => { const g = productGroups.find(pg => pg.id === id.group_id); return g ? <span className="text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded-full ml-2">{g.name}</span> : null; })()}
                          </div>
                        ))}
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const sub = (c.subs || [])[0];
                          if (!sub) return <span className="text-xs text-slate-400">No Plan</span>;
                          const plan = plans.find(p => p.id === sub.product_id);
                          return plan ? (
                            <div className="text-sm">
                              <div className="font-semibold text-slate-700">{plan.name}</div>
                              <div className="text-xs text-slate-500">₹{plan.base_cost} / {plan.validity_days} days</div>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">Unknown Plan</span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4">
                        {(c.subs || []).map((s) => (
                          <div key={s.id} className="text-sm flex items-center gap-2">
                            <Badge tone={s.status === "active" ? "success" : "warn"}>{s.status}</Badge>
                            <span className="text-xs text-slate-500">till {s.plan_end}</span>
                          </div>
                        ))}
                        {(!c.subs || c.subs.length === 0) && <span className="text-xs text-slate-400">No active subscription</span>}
                      </td>
                      <td className="px-6 py-4">
                        {(c.ids || [])[0] && (
                          <div className="flex flex-col gap-1 items-start">
                            <Badge tone="info">{c.ids[0].ref_type}</Badge>
                            {c.ids[0].ref_type !== 'admin' && c.ids[0].ref_name && (
                              <span className="text-xs text-slate-500 font-medium max-w-[120px] truncate" title={c.ids[0].ref_name}>
                                {c.ids[0].ref_name}
                              </span>
                            )}
                            {c.ids[0].lead_agent_name && (
                              <div className="flex items-center gap-1 mt-1 bg-slate-100 rounded px-1.5 py-0.5">
                                <span className="text-[10px] font-bold uppercase text-slate-400">Agent:</span>
                                <span className="text-[10px] text-slate-600 font-medium truncate max-w-[90px]" title={c.ids[0].lead_agent_name}>
                                  {c.ids[0].lead_agent_name}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {c.request_date ? (
                          <div className="text-xs text-slate-500">
                            {new Date(c.request_date).toLocaleString('en-IN', {
                              year: 'numeric', month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {c.approve_date ? (
                          <div className="text-xs text-slate-500">
                            {new Date(c.approve_date).toLocaleString('en-IN', {
                              year: 'numeric', month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => openEditModal(c)} className="text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 transition-colors">
                            <Pencil className="w-3 h-3" /> Edit
                          </button>
                          <button onClick={() => handleDelete(c)} className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 transition-colors">
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                          {c.ids && c.ids[0] && (
                            <button
                              onClick={() => handleCheckSub(c.ids[0].id)}
                              disabled={checkingApi === c.ids[0].id}
                              className="text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 transition-colors disabled:opacity-50"
                              title="Check DishTV Subscription"
                            >
                              {checkingApi === c.ids[0].id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Check Details
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {customers.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-10 text-slate-400 text-sm">
                      {custSearchApi ? "No customers match your search" : "No active customers yet"}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="border-t border-slate-200 bg-slate-50">
              <Pagination page={custPage} pageCount={custPageCount} onChange={setCustPage} total={custTotal} />
            </div>
          </div>
        </>
      )}

      {/* ── APPROVE MODAL ── */}
      {approveModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <form onSubmit={handleApprove} className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="font-heading font-bold text-xl text-brand-nav">Approve Customer</h3>
            <p className="text-sm text-slate-500 mb-4 mt-1">Assign IDs to activate <strong>{approveModal.name}</strong></p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Customer ID (DTH ID) *</label>
                <input required placeholder="Enter DTH ID" value={approveForm.customer_id} onChange={e => setApproveForm(p => ({ ...p, customer_id: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Box Number *</label>
                <input required placeholder="Enter STB Box Number" value={approveForm.box_number} onChange={e => setApproveForm(p => ({ ...p, box_number: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono" />
              </div>
              {approveModal.submitted_by_role === 'agent' && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Assign Dealer *</label>
                  <select required value={approveForm.assigned_dealer_id} onChange={e => setApproveForm(p => ({ ...p, assigned_dealer_id: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm">
                    <option value="">— Choose a Dealer —</option>
                    <option value="admin">Direct to Admin</option>
                    {dealers.map(d => <option key={d.id} value={d.id}>{d.user?.name} ({d.type})</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Product Group</label>
                <select value={approveForm.group_id} onChange={e => setApproveForm(p => ({ ...p, group_id: e.target.value, plan_id: "" }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm">
                  <option value="">— Choose a Group —</option>
                  {productGroups.filter(g => g.is_active).map(g => <option key={g.id} value={g.id}>{g.name} ({g.operator})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Subscription Plan</label>
                <select value={approveForm.plan_id} onChange={e => setApproveForm(p => ({ ...p, plan_id: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm"
                  disabled={!approveForm.group_id}>
                  <option value="">{approveForm.group_id ? "— Choose Plan —" : "— Select Group First —"}</option>
                  {plans.filter(p => p.is_active && p.group_id === approveForm.group_id && p.plan_type !== 'recharge').map(p => <option key={p.id} value={p.id}>{p.name} (₹{p.base_cost})</option>)}
                </select>
              </div>
              <div className="bg-blue-50 text-blue-800 p-3 rounded-lg text-xs border border-blue-100">
                A 6-digit password will be auto-generated and sent via SMS to <strong>{approveModal.mobile}</strong> upon approval.
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setApproveModal(null)} className="flex-1 border border-slate-200 text-slate-600 rounded-lg px-4 py-2.5 font-semibold hover:bg-slate-50">Cancel</button>
              <button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2.5 font-semibold">Approve & Activate</button>
            </div>
          </form>
        </div>
      )}

      {/* ── EDIT CUSTOMER MODAL ── */}
      {editModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <form onSubmit={handleEdit} className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-xl">
            <h3 className="font-heading font-bold text-xl text-brand-nav">Edit Customer</h3>
            <p className="text-sm text-slate-500 mb-4 mt-1">Update all details for <strong>{editModal.user?.name}</strong></p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
                <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Mobile</label>
                <input value={editForm.mobile} onChange={e => setEditForm(f => ({ ...f, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="md:col-span-2 border-t border-slate-100 pt-3 mt-1">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">DTH Connection</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">VC / DTH ID</label>
                <input value={editForm.dth_id} onChange={e => setEditForm(f => ({ ...f, dth_id: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Box Number (STB)</label>
                <input value={editForm.box_number} onChange={e => setEditForm(f => ({ ...f, box_number: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Operator</label>
                <select value={editForm.operator} onChange={e => setEditForm(f => ({ ...f, operator: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                  <option>Dish TV</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Assign to Dealer</label>
                <SearchableSelect
                  value={editForm.dealer_id}
                  onChange={val => setEditForm(f => ({ ...f, dealer_id: val }))}
                  options={[{ label: "Direct to Admin", value: "" }, ...dealers.map(d => ({ label: `${d.user?.name} (${d.type})`, value: d.id }))]}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Product Group</label>
                <select value={editForm.group_id} onChange={e => setEditForm(f => ({ ...f, group_id: e.target.value, plan_id: "" }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                  <option value="">— No Group (All Products) —</option>
                  {productGroups.filter(g => g.is_active).map(g => <option key={g.id} value={g.id}>{g.name} ({g.operator})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Subscription Plan</label>
                <select value={editForm.plan_id} onChange={e => setEditForm(f => ({ ...f, plan_id: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  disabled={!editForm.group_id}>
                  <option value="">{editForm.group_id ? "— Keep Current / No Plan —" : "— Select Group First —"}</option>
                  {plans.filter(p => p.is_active && p.group_id === editForm.group_id).map(p => <option key={p.id} value={p.id}>{p.name} (₹{p.base_cost})</option>)}
                </select>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setEditModal(null)} className="flex-1 border border-slate-200 text-slate-600 rounded-lg px-4 py-2.5 font-semibold hover:bg-slate-50">Cancel</button>
              <button type="submit" className="flex-1 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-lg px-4 py-2.5 font-semibold">Save Changes</button>
            </div>
          </form>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setRejectModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg text-slate-800">Reject Request</h3>
                <p className="text-xs text-slate-500">For {rejectModal.name}</p>
              </div>
            </div>
            <form onSubmit={submitReject}>
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 mb-2">Reason for rejection (optional)</label>
                <textarea
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 outline-none resize-none"
                  rows="3"
                  placeholder="e.g. Invalid DTH ID, Duplicate request..."
                />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setRejectModal(null)} className="flex-1 px-4 py-2 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-sm transition-colors">
                  Confirm Reject
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Check Subscription Result Modal */}
      {checkSubModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200" style={{ border: "1px solid #e2e8f0" }}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="font-heading font-bold text-lg text-brand-nav flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" /> API Details
              </h2>
              <button onClick={() => setCheckSubModal(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-center">
                <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Subscription Expiry Date</p>
                <p className="text-xl font-bold text-green-700">{checkSubModal.last_FT_Date || 'N/A'}</p>
                <p className="text-xs text-green-600/70 mt-1">Successfully updated in database.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 text-center">
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wide mb-1">Base Package</p>
                  <p className="text-lg font-bold text-slate-700">₹{checkSubModal.packagePriceWithoutTax}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 text-center">
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wide mb-1">Extra Channels</p>
                  <p className="text-lg font-bold text-slate-700">₹{checkSubModal.totalAlaCartePriceWithoutTax}</p>
                </div>
              </div>

              <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100 text-center">
                <p className="text-xs font-bold text-indigo-600 uppercase tracking-wide mb-1">Total Recharge Amount (Inc. 18% GST)</p>
                <p className="text-2xl font-heading font-black text-indigo-900">₹{checkSubModal.total_recharge_amount}</p>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-100 flex justify-end">
              <button onClick={() => setCheckSubModal(null)} className="px-5 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primaryHover rounded-lg transition shadow-sm">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── BULK UPLOAD MODAL ── */}
      {showBulkUpload && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => { if (!bulkUploading) { setShowBulkUpload(false); } }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Upload className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-slate-800">Bulk Upload Requests</h3>
                  <p className="text-xs text-slate-500">Upload Excel file to create multiple requests</p>
                </div>
              </div>
              <button onClick={() => { if (!bulkUploading) setShowBulkUpload(false); }} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Step 1: Download Template */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-start gap-3">
                  <FileSpreadsheet className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-blue-800">Step 1: Download the Excel template</p>
                    <p className="text-xs text-blue-600 mt-1">Download the template, fill in customer details, then upload it below.</p>
                    <button onClick={handleDownloadTemplate} disabled={downloadingTemplate}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50">
                      {downloadingTemplate ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                      Download Template
                    </button>
                  </div>
                </div>
              </div>

              {/* Step 2: Upload File */}
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-2">Step 2: Upload filled Excel file</p>
                <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                  bulkFile ? 'border-green-400 bg-green-50' : 'border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-slate-400'
                }`}>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={e => { setBulkFile(e.target.files[0] || null); setBulkResult(null); }}
                    disabled={bulkUploading}
                  />
                  {bulkFile ? (
                    <div className="text-center">
                      <FileSpreadsheet className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-green-700">{bulkFile.name}</p>
                      <p className="text-xs text-green-600 mt-0.5">{(bulkFile.size / 1024).toFixed(1)} KB • Click to change</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">Click to select Excel file</p>
                      <p className="text-xs text-slate-400 mt-0.5">.xlsx files only • Max 500 rows</p>
                    </div>
                  )}
                </label>
              </div>

              {/* Upload Button */}
              <button
                onClick={handleBulkUpload}
                disabled={!bulkFile || bulkUploading}
                className="w-full py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2"
              >
                {bulkUploading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                ) : (
                  <><Upload className="w-4 h-4" /> Upload & Create Requests</>
                )}
              </button>

              {/* Results */}
              {bulkResult && (
                <div className="space-y-3">
                  {/* Summary */}
                  <div className="flex gap-3">
                    {bulkResult.success > 0 && (
                      <div className="flex-1 bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
                        <p className="text-lg font-bold text-green-700">{bulkResult.success}</p>
                        <p className="text-xs text-green-600">Successful</p>
                      </div>
                    )}
                    {bulkResult.failed > 0 && (
                      <div className="flex-1 bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                        <XCircle className="w-5 h-5 text-red-500 mx-auto mb-1" />
                        <p className="text-lg font-bold text-red-700">{bulkResult.failed}</p>
                        <p className="text-xs text-red-600">Failed</p>
                      </div>
                    )}
                  </div>

                  {/* Error Details */}
                  {bulkResult.errors && bulkResult.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-xl overflow-hidden">
                      <div className="px-4 py-2.5 bg-red-100 border-b border-red-200 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                        <span className="text-sm font-semibold text-red-800">Error Details</span>
                      </div>
                      <div className="max-h-40 overflow-auto">
                        {bulkResult.errors.map((err, i) => (
                          <div key={i} className="px-4 py-2 border-b border-red-100 last:border-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-red-600 bg-red-200 px-1.5 py-0.5 rounded">Row {err.row}</span>
                              <span className="text-xs font-semibold text-red-700">{err.name}</span>
                            </div>
                            <ul className="mt-1 space-y-0.5">
                              {err.errors.map((e, j) => (
                                <li key={j} className="text-xs text-red-600 pl-2">• {e}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
